import { doc, getDoc, serverTimestamp, setDoc, writeBatch } from "firebase/firestore";
import { createShareCode } from "RandomId";
import { bootstrapUserShell } from "../features/userSlice";
import { db } from "../firebase";
import { FIREBASE_COLLECTIONS } from "../config/firebaseCollections";
import {
  buildDefaultAnnouncements,
  buildDefaultClasses,
  buildDefaultSettings,
  convertEmailToLowercase,
} from "./signupDefaults";
import { createDefaultProductTourState } from "./productTour";
import { createNotApplicableLegacyScheduleJuly2026ConversionState } from "./legacyScheduleJuly2026Conversion";
import { initializeNewUserSchedulingData } from "./scheduleCalendarFirestore";
import { reportAppError } from "./reportAppError";
import { getSignupMobileLeadValue, getSignupRuntimeDeviceLeadFields } from "./signupDevice";

export function splitDisplayName(displayName = "") {
  const nameParts = String(displayName || "").trim().split(/\s+/).filter(Boolean);
  const [firstName = "", ...lastNameParts] = nameParts;

  return {
    firstName,
    lastName: lastNameParts.join(" "),
  };
}

export function isUserShellProvisioned(userData = {}) {
  return Boolean(
    userData
    && Array.isArray(userData.orderOfClasses)
    && userData.orderOfClasses.length > 0
    && userData.settings
    && userData.provisioningIncomplete !== true
  );
}

/**
 * Only brand-new account shells may become Meta-registration eligible.
 * Existing users missing the field must never be backfilled as eligible.
 * claimed / pending / fired / eligible must never be overwritten with eligible.
 */
export function isMetaRegistrationStatusLockedAgainstEligible(status) {
  return (
    status === "eligible"
    || status === "claimed"
    || status === "pending"
    || status === "fired"
  );
}

export function shouldSetMetaRegistrationEligible(existingData) {
  if (!existingData) {
    return true;
  }
  if (isMetaRegistrationStatusLockedAgainstEligible(existingData.metaRegistrationTrackingStatus)) {
    return false;
  }
  if (existingData.metaRegistrationTrackingStatus) {
    return false;
  }
  return existingData.provisioningIncomplete === true;
}

/** Fields to merge when seeding eligibility. Empty when a downgrade would result. */
export function buildMetaRegistrationEligibleWrite(existingData) {
  if (!shouldSetMetaRegistrationEligible(existingData)) {
    return {};
  }
  return { metaRegistrationTrackingStatus: "eligible" };
}

function buildProvisioningProfile(authUser, profile = {}) {
  const displayNameProfile = splitDisplayName(authUser?.displayName);

  return {
    firstName: profile.firstName || displayNameProfile.firstName || "Teacher",
    lastName: profile.lastName || displayNameProfile.lastName || "",
    email: convertEmailToLowercase(profile.email || authUser?.email || ""),
    leadSource: profile.leadSource || "",
  };
}

export async function waitForUserDocument(userId, maxRetries = 10, delay = 500) {
  const userRef = doc(db, FIREBASE_COLLECTIONS.USERS, userId);

  for (let i = 0; i < maxRetries; i += 1) {
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      return userSnap;
    }
    await new Promise((resolve) => setTimeout(resolve, delay));
  }

  return null;
}

export async function bootstrapUserShellAfterSignup(dispatch, uid) {
  if (!uid || !dispatch) return;
  await dispatch(bootstrapUserShell(uid));
}

async function writeDefaultClassDocuments(userId, classes) {
  for (const classData of classes) {
    const { contentBoxes, classId, ...classWithoutBoxes } = classData;
    const classRef = doc(db, FIREBASE_COLLECTIONS.USERS, userId, FIREBASE_COLLECTIONS.CLASSES, classId);
    const existingClassSnap = await getDoc(classRef);
    if (existingClassSnap.exists()) {
      continue;
    }

    const contentBoxesOrder = contentBoxes.map((box) => box.contentBoxId);
    const classBatch = writeBatch(db);

    classBatch.set(classRef, {
      classId,
      ...classWithoutBoxes,
      contentBoxesOrder,
    });

    if (contentBoxes?.length) {
      for (const box of contentBoxes) {
        const contentBoxRef = doc(
          db,
          FIREBASE_COLLECTIONS.USERS,
          userId,
          FIREBASE_COLLECTIONS.CLASSES,
          classId,
          FIREBASE_COLLECTIONS.CONTENT_BOXES,
          box.contentBoxId
        );
        classBatch.set(contentBoxRef, box);
      }
    }

    await classBatch.commit();
  }
}

/**
 * Idempotent Auth → usersNew provisioning.
 * Writes the root usersNew doc first (critical path), then defaults/classes.
 * Completes shells left incomplete by the Auth onCreate backstop or a partial client write.
 */
export async function ensureUserDocumentForAuthUser(authUser, profile = {}) {
  if (!authUser?.uid) {
    throw new Error("Missing authenticated user ID.");
  }

  const userId = authUser.uid;
  const userRef = doc(db, FIREBASE_COLLECTIONS.USERS, userId);
  const existingUserSnap = await getDoc(userRef);
  const existingData = existingUserSnap.exists() ? (existingUserSnap.data() || {}) : null;

  if (existingData && isUserShellProvisioned(existingData)) {
    return {
      created: false,
      userId,
      profile: buildProvisioningProfile(authUser, profile),
      userData: existingData,
    };
  }

  const resolvedProfile = buildProvisioningProfile(authUser, {
    ...profile,
    firstName: profile.firstName || existingData?.firstName,
    lastName: profile.lastName || existingData?.lastName,
    email: profile.email || existingData?.email,
    leadSource: profile.leadSource || existingData?.leadData?.leadSource,
  });
  const settings = existingData?.settings || buildDefaultSettings();
  const announcements = existingData?.announcements?.length
    ? existingData.announcements
    : buildDefaultAnnouncements();
  const classes = buildDefaultClasses();
  const orderOfClasses = existingData?.orderOfClasses?.length
    ? existingData.orderOfClasses
    : classes.map((cls) => cls.classId);
  const runtimeDeviceLeadFields = existingData?.leadData?.runtimeSignupDevice
    ? {}
    : getSignupRuntimeDeviceLeadFields();

  // Critical path: persist a root usersNew document before heavier setup.
  await setDoc(userRef, {
    firstName: resolvedProfile.firstName,
    lastName: resolvedProfile.lastName,
    email: resolvedProfile.email,
    accountCreated: existingData?.accountCreated || serverTimestamp(),
    premium: existingData?.premium ?? false,
    trial: existingData?.trial ?? true,
    tags: existingData?.tags?.length ? existingData.tags : ["Trial"],
    shareCode: existingData?.shareCode || createShareCode(),
    uid: userId,
    leadData: {
      ...(existingData?.leadData || {}),
      leadSource: existingData?.leadData?.leadSource || resolvedProfile.leadSource,
      // Prefer an already-resolved boolean; otherwise detect now (fills authOnCreate empty mobile).
      mobile: (existingData?.leadData?.mobile === true || existingData?.leadData?.mobile === false)
        ? existingData.leadData.mobile
        : getSignupMobileLeadValue(),
      ...runtimeDeviceLeadFields,
      attributionSummary: existingData?.leadData?.attributionSummary || profile.attributionSummary || "",
    },
    attribution: existingData?.attribution ?? profile.attribution ?? null,
    countryAttribution: existingData?.countryAttribution ?? profile.countryAttribution ?? null,
    announcements,
    orderOfClasses,
    settings,
    productTour: existingData?.productTour || createDefaultProductTourState(),
    legacyScheduleJuly2026Conversion: createNotApplicableLegacyScheduleJuly2026ConversionState(),
    ...buildMetaRegistrationEligibleWrite(existingData),
    provisioningIncomplete: true,
  }, { merge: true });

  try {
    await initializeNewUserSchedulingData(userId, settings);
    await writeDefaultClassDocuments(userId, classes);
    await setDoc(userRef, {
      provisioningIncomplete: false,
      provisionedBy: existingData?.provisionedBy === "authOnCreate"
        ? "clientEnsure"
        : (existingData?.provisionedBy || "clientEnsure"),
    }, { merge: true });
  } catch (error) {
    reportAppError(error, {
      feature: "user-provisioning",
      action: "complete-user-shell",
      uid: userId,
      dedupeKey: `user-provisioning:${userId}`,
    });
    throw error;
  }

  await waitForUserDocument(userId);

  return {
    created: true,
    userId,
    profile: resolvedProfile,
    userData: null,
  };
}

export async function ensureUserDocumentWithRetries(authUser, profile = {}, {
  maxAttempts = 3,
  delayMs = 750,
} = {}) {
  let lastError = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await ensureUserDocumentForAuthUser(authUser, profile);
    } catch (error) {
      lastError = error;
      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, delayMs * attempt));
      }
    }
  }

  throw lastError || new Error("Unable to provision user document.");
}
